import React, { useState, useEffect } from 'react';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';

const PopupAdvertisement = ({ isVisible, onClose }) => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 59
  });

  useEffect(() => {
    if (!isVisible) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let newHours = prev?.hours;
        let newMinutes = prev?.minutes;
        let newSeconds = prev?.seconds - 1;

        if (newSeconds < 0) {
          newSeconds = 59;
          newMinutes -= 1;
        }

        if (newMinutes < 0) {
          newMinutes = 59;
          newHours -= 1;
        }

        if (newHours < 0) {
          newHours = 23;
          newMinutes = 59;
          newSeconds = 59;
        }

        return {
          hours: newHours,
          minutes: newMinutes,
          seconds: newSeconds
        };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isVisible]);

  const handleWhatsAppBooking = () => {
    const message = encodeURIComponent("Hi! I'm interested in your limited time offer. Can you please provide more details about the available packages and pricing?");
    window.open(`https://wa.me/918277385225?text=${message}`, '_blank');
  };

  const handleCallNow = () => {
    window.open('tel:+918277385225', '_self');
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative bg-white rounded-xl sm:rounded-2xl shadow-organic-xl max-w-xs sm:max-w-sm md:max-w-md w-full mx-2 sm:mx-4 overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-2 sm:top-4 right-2 sm:right-4 z-10 w-6 h-6 sm:w-8 sm:h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-organic"
          aria-label="Close popup"
        >
          <Icon name="X" size={12} className="text-gray-600 sm:hidden" />
          <Icon name="X" size={16} className="text-gray-600 hidden sm:block" />
        </button>

        {/* Header Section */}
        <div className="bg-gradient-to-r from-primary to-secondary text-white p-4 sm:p-6 pb-3 sm:pb-4">
          <div className="text-center">
            <h3 className="text-xl sm:text-2xl font-heading font-bold mb-1 sm:mb-2">
              🎉 Limited Time Offer!
            </h3>
            <p className="text-white/90 font-body text-sm sm:text-base">
              Special discount on all resort bookings
            </p>
          </div>
        </div>

        {/* Timer Section */}
        <div className="px-4 sm:px-6 pb-3 sm:pb-4 bg-gradient-to-r from-primary to-secondary text-white">
          <div className="bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4">
            <p className="text-center text-xs sm:text-sm font-medium mb-2 sm:mb-3 text-white/90">Offer ends in:</p>
            <div className="flex justify-center space-x-2 sm:space-x-4">
              <div className="text-center">
                <div className="bg-white/30 rounded-lg p-1 sm:p-2 min-w-[40px] sm:min-w-[50px]">
                  <div className="text-lg sm:text-xl font-bold text-white">
                    {timeLeft?.hours?.toString()?.padStart(2, '0')}
                  </div>
                  <div className="text-[10px] sm:text-xs text-white/80">Hours</div>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-white/30 rounded-lg p-1 sm:p-2 min-w-[40px] sm:min-w-[50px]">
                  <div className="text-lg sm:text-xl font-bold text-white">
                    {timeLeft?.minutes?.toString()?.padStart(2, '0')}
                  </div>
                  <div className="text-[10px] sm:text-xs text-white/80">Mins</div>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-white/30 rounded-lg p-1 sm:p-2 min-w-[40px] sm:min-w-[50px]">
                  <div className="text-lg sm:text-xl font-bold text-white">
                    {timeLeft?.seconds?.toString()?.padStart(2, '0')}
                  </div>
                  <div className="text-[10px] sm:text-xs text-white/80">Secs</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-4 sm:p-6">
          <div className="text-center mb-4 sm:mb-6">
            <h4 className="text-lg sm:text-xl font-heading font-semibold text-gray-900 mb-2 sm:mb-3">
              Up to 30% Off Resort Stays!
            </h4>
            <div className="space-y-1 sm:space-y-2 text-gray-600 font-body text-xs sm:text-sm">
              <p className="flex items-center justify-center">
                <Icon name="Check" size={12} className="text-primary mr-1 sm:hidden" />
                <Icon name="Check" size={16} className="text-primary mr-2 hidden sm:block" />
                Premium accommodations
              </p>
              <p className="flex items-center justify-center">
                <Icon name="Check" size={12} className="text-primary mr-1 sm:hidden" />
                <Icon name="Check" size={16} className="text-primary mr-2 hidden sm:block" />
                Adventure activities included
              </p>
              <p className="flex items-center justify-center">
                <Icon name="Check" size={12} className="text-primary mr-1 sm:hidden" />
                <Icon name="Check" size={16} className="text-primary mr-2 hidden sm:block" />
                Complimentary meals
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2 sm:space-y-3">
            <Button
              variant="default"
              size="sm"
              iconName="MessageCircle"
              iconPosition="left"
              fullWidth
              onClick={handleWhatsAppBooking}
              className="bg-green-600 hover:bg-green-700 text-white font-medium text-xs sm:text-sm py-2 sm:py-3"
            >
              Book on WhatsApp
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              iconName="Phone"
              iconPosition="left"
              fullWidth
              onClick={handleCallNow}
              className="border-primary text-primary hover:bg-primary hover:text-white font-medium text-xs sm:text-sm py-2 sm:py-3"
            >
              <span className="sm:hidden">Call Now</span>
              <span className="hidden sm:inline">Call Now - +91 8277385225</span>
            </Button>
          </div>

          {/* Terms */}
          <p className="text-[10px] sm:text-xs text-gray-500 text-center mt-3 sm:mt-4 font-caption">
            *Terms and conditions apply. Valid for limited period only.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PopupAdvertisement;